import { useMutation, useQuery } from '@apollo/client'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import * as Yup from 'yup'
import {
  CREATE_SCHEDULE_GROUP,
  GET_SCHEDULE_GROUP_BY_ID,
  UPDATE_SCHEDULE_GROUP,
} from '../../../../../graphql/Catalog/Employees/Groups/scheduleGroups'
import { useHistory, useParams } from 'react-router-dom'
import { toastSweetAlert } from '../../../../Helpers/ToastSweetAlert'
import { useLoading } from '../../../../../LoadingOverlay/LoadingOverlayContext'
import useBackPage from '../../../../../CustomHooks/BackPage/useBackPage'
import Box from '../../../../Global/Box'
import InputController from '../../../../Global/InputController'
import ContentHeader from '../../../../Layout/ContentHeader'

const optionsDaysOfWeek = [
  {
    value: 'MONDAY',
    label: 'Lunes',
  },
  {
    value: 'TUESDAY',
    label: 'Martes',
  },
  {
    value: 'WEDNESDAY',
    label: 'Miércoles',
  },
  {
    value: 'THURSDAY',
    label: 'Jueves',
  },
  {
    value: 'FRIDAY',
    label: 'Viernes',
  },
  {
    value: 'SATURDAY',
    label: 'Sábado',
  },
  {
    value: 'SUNDAY',
    label: 'Domingo',
  },
]

const ScheduleGroupsNew = () => {
  const { id: _id } = useParams()
  const history = useHistory()
  //CUSTOM HOOKS
  const { showLoading, hideLoading } = useLoading()
  const backPage = useBackPage()

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('El nombre es requerido'),
    description: Yup.string().required('La descripción es requerida'),
    start_day_of_week: Yup.string()
      .oneOf(
        optionsDaysOfWeek.map((day) => day.value),
        'Día de inicio inválido'
      )
      .required('El día de inicio es requerido'),
    end_day_of_week: Yup.string()
      .oneOf(
        optionsDaysOfWeek.map((day) => day.value),
        'Día de fin inválido'
      )
      .required('El día de fin es requerido'),
    start_time: Yup.string()
      // .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Hora de inicio inválida')
      .required('La hora de inicio es requerida'),
    end_time: Yup.string()
      // .matches(/^([01]\d|2[0-3]):([0-5]\d)$/, 'Hora de fin inválida')
      .required('La hora de fin es requerida'),
    tolerance_minutes: Yup.number()
      .min(0, 'Los minutos de tolerancia no pueden ser negativos')
      .max(60, 'Los minutos de tolerancia no pueden ser mayores a 60')
      .required('Los minutos de tolerancia son requeridos'),
  })

  const {
    reset,
    handleSubmit,
    control,
    formState: { errors },
    methods,
    setValue,
  } = useForm({
    resolver: yupResolver(validationSchema),
  })

  const {
    loading: loadingScheduleGroup,
    error: errorScheduleGroup,
    data: dataScheduleGroup,
  } = useQuery(GET_SCHEDULE_GROUP_BY_ID, {
    variables: {
      getScheduleGroupByIdId: _id ? parseInt(_id) : null,
    },
    skip: !_id,
    fetchPolicy: 'cache-and-network',
  })

  const [createSchedule] = useMutation(CREATE_SCHEDULE_GROUP)
  const [updateSchedule] = useMutation(UPDATE_SCHEDULE_GROUP)

  useEffect(() => {
    try {
      if (!_id) return
      if (loadingScheduleGroup) return
      if (errorScheduleGroup) throw new Error(errorScheduleGroup.message)
      const { getScheduleGroupById } = dataScheduleGroup
      reset({
        name: getScheduleGroupById.name,
        description: getScheduleGroupById.description,
        start_day_of_week:
          getScheduleGroupById.GroupsToGroupDetails.start_day_of_week,
        end_day_of_week:
          getScheduleGroupById.GroupsToGroupDetails.end_day_of_week,
        start_time: getScheduleGroupById.GroupsToGroupDetails.start_time,
        end_time: getScheduleGroupById.GroupsToGroupDetails.end_time,
        tolerance_minutes:
          getScheduleGroupById.GroupsToGroupDetails.tolerance_minutes,
      })

      setValue(
        'start_time',
        getScheduleGroupById.GroupsToGroupDetails.start_time
      )
      setValue('end_time', getScheduleGroupById.GroupsToGroupDetails.end_time)
    } catch (error) {
      return toastSweetAlert({
        mode: 'error',
        message: error.message,
      })
    }
  }, [loadingScheduleGroup, errorScheduleGroup, dataScheduleGroup])

  const onSubmit = async (data) => {
    showLoading()
    try {
      if (_id) {
        const { data: updateData } = await updateSchedule({
          variables: {
            updateScheduleGroupEmployeesId: parseInt(_id),
            input: {
              name: data.name,
              description: data.description,
              inputDetails: {
                start_day_of_week: data.start_day_of_week,
                end_day_of_week: data.end_day_of_week,
                start_time: data.start_time,
                end_time: data.end_time,
                tolerance_minutes: data.tolerance_minutes,
              },
            },
          },
        })

        if (updateData.updateScheduleGroupEmployees) {
          return toastSweetAlert(
            {
              mode: 'ok',
              message: 'Grupo de horario actualizado correctamente',
            },
            history.push(backPage)
          )
        }
      }

      const { data: createData } = await createSchedule({
        variables: {
          input: {
            name: data.name,
            description: data.description,
            inputDetails: {
              start_day_of_week: data.start_day_of_week,
              end_day_of_week: data.end_day_of_week,
              start_time: data.start_time,
              end_time: data.end_time,
              tolerance_minutes: data.tolerance_minutes,
            },
          },
        },
      })

      if (createData.createScheduleGroupEmployees) {
        return toastSweetAlert(
          {
            mode: 'ok',
            message: 'Grupo de horario creado correctamente',
          },
          history.push(backPage)
        )
      }
    } catch (error) {
      return toastSweetAlert({
        mode: 'error',
        message: error.message,
      })
    } finally {
      hideLoading()
    }
  }

  return (
    <>
      <ContentHeader
        title=""
        breadcrumb=""
        windowTitle={_id ? 'Editar Grupo de Horario' : 'Nuevo Grupo de Horario'}
      />
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box
            title={_id ? 'Editar Grupo de Horario' : 'Nuevo Grupo de Horario'}
            BackPageHeader
            btnSubmit
            btnSubmitText={_id ? 'Actualizar' : 'Crear'}
            content={
              <>
                <div className="row">
                  <div className="mb-3 col-lg-6">
                    <InputController
                      label={'Nombre'}
                      name={'name'}
                      control={control}
                      placeholder={'Nombre del grupo de horario'}
                    />
                  </div>
                  <div className="mb-3 col-lg-6">
                    <InputController
                      label={'Descripción'}
                      name={'description'}
                      inputType={'textarea'}
                      control={control}
                      placeholder={'Descripción del grupo de horario'}
                    />
                  </div>
                  <div className="mb-3 col-lg-6">
                    <InputController
                      label={'Día de Inicio'}
                      name={'start_day_of_week'}
                      control={control}
                      inputType={'choosen'}
                      options={optionsDaysOfWeek}
                      placeholderTxt="Día de inicio del grupo de horario"
                    />
                  </div>
                  <div className="mb-3 col-lg-6">
                    <InputController
                      label={'Día de Fin'}
                      name={'end_day_of_week'}
                      control={control}
                      inputType={'choosen'}
                      options={optionsDaysOfWeek}
                      placeholder={'Día de fin del grupo de horario'}
                    />
                  </div>
                  <div className="mb-3 col-lg-4">
                    <InputController
                      label={'Hora de Inicio'}
                      name={'start_time'}
                      control={control}
                      type={'time'}
                    />
                  </div>
                  <div className="mb-3 col-lg-4">
                    <InputController
                      label={'Hora de Fin'}
                      name={'end_time'}
                      control={control}
                      type={'time'}
                    />
                  </div>
                  <div className="mb-3 col-lg-4">
                    <InputController
                      label={'Minutos de Tolerancia'}
                      name={'tolerance_minutes'}
                      control={control}
                      type={'number'}
                      placeholder={'Minutos de tolerancia'}
                      min={0}
                      max={60}
                    />
                  </div>
                </div>
              </>
            }
          />
        </form>
      </FormProvider>
    </>
  )
}

export default ScheduleGroupsNew
